import { useEffect, useState, useCallback, useSyncExternalStore } from 'react';
import { getStrictContext } from '@/lib/get-strict-context';

type ThemePreference = 'light' | 'dark' | 'system';
type ResolvedTheme = 'light' | 'dark';

interface ThemeContextValue {
  theme: ThemePreference;
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: ThemePreference) => void;
  isDark: boolean;
}

const STORAGE_KEY = 'robinhoot-theme';

const [ThemeContextProvider, useTheme] = getStrictContext<ThemeContextValue>('ThemeProvider');

function getInitialTheme(): ThemePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'dark' || stored === 'light' || stored === 'system') return stored;
  } catch {
    // localStorage unavailable
  }
  return 'light';
}

const mediaQuery =
  typeof window !== 'undefined' ? window.matchMedia('(prefers-color-scheme: dark)') : null;

function subscribeToMediaQuery(cb: () => void) {
  mediaQuery?.addEventListener('change', cb);
  return () => mediaQuery?.removeEventListener('change', cb);
}

function getSystemIsDark() {
  return mediaQuery?.matches ?? false;
}

const THEME_COLORS: Record<ResolvedTheme, string> = {
  light: '#f4f5f6',
  dark: '#0f172a',
};

function applyThemeToDOM(resolved: ResolvedTheme) {
  const root = document.documentElement;
  if (resolved === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }

  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', THEME_COLORS[resolved]);
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<ThemePreference>(getInitialTheme);

  const systemIsDark = useSyncExternalStore(subscribeToMediaQuery, getSystemIsDark, () => false);
  const resolvedTheme: ResolvedTheme =
    theme === 'system' ? (systemIsDark ? 'dark' : 'light') : theme;

  useEffect(() => {
    applyThemeToDOM(resolvedTheme);
  }, [resolvedTheme]);

  const setTheme = useCallback((next: ThemePreference) => {
    setThemeState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // localStorage unavailable
    }
  }, []);

  return (
    <ThemeContextProvider
      value={{ theme, resolvedTheme, setTheme, isDark: resolvedTheme === 'dark' }}
    >
      {children}
    </ThemeContextProvider>
  );
}

export { useTheme };
export type { ThemePreference, ResolvedTheme };
